import { useEffect, useState } from 'react';
import Btn from './ui/Btn';
import DictationButton from './DictationButton';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Onglet « Note generale » du hub dossier (Sprint 2 §9.2).
 *
 * Un seul espace de texte libre par dossier, partage entre collaborateurs
 * pour les points transverses (memos, points d'attention, todo). Different
 * du systeme ``dossier_notes`` qui est multi-entrees attache aux pieces.
 *
 * Persiste via PATCH /dossiers/{id} avec le champ ``general_note``.
 * ``""`` ou ``null`` efface le contenu.
 */

type Props = {
  dossierId: string;
  initial: string | null;
  onSaved: () => void;
};

export default function NoteGeneraleTab({ dossierId, initial, onSaved }: Props) {
  const [draft, setDraft] = useState<string>(initial ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initial ?? '');
  }, [initial, dossierId]);

  const dirty = draft !== (initial ?? '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/dossiers/${dossierId}`, { general_note: draft || null });
      toast.success('Note enregistree');
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur reseau'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-rule bg-paper-card p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-[20px] text-text-primary">Note generale</h2>
          <span className="text-[12px] text-text-tertiary">
            Espace de travail commun aux collaborateurs du dossier
          </span>
        </div>
        <p className="mt-1 text-[13px] text-text-secondary">
          Points d&apos;attention transverses, memos, plan d&apos;action. Pour des notes attachees a
          une piece, utilise les notes de piece dans l&apos;editeur.
        </p>
      </div>

      <div className="relative">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Tape ici. La note est partagee avec tous les collaborateurs du dossier."
          rows={20}
          className="w-full resize-y rounded-md border border-rule-strong bg-paper-elevated p-3 pr-14 font-mono text-[13px] leading-[1.5] text-text-primary focus:border-ink focus:outline-none"
        />
        <div className="absolute top-2 right-2">
          <DictationButton
            size="sm"
            onTranscript={(chunk) => setDraft((prev) => (prev ? prev + ' ' : '') + chunk)}
            title="Dicter la note générale"
          />
        </div>
      </div>

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-[13px] text-cat-def">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Btn variant="primary" onClick={() => void save()} disabled={!dirty || saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Btn>
        {dirty && !saving && (
          <span className="text-[12px] text-text-tertiary">Modifications non enregistrees</span>
        )}
      </div>
    </div>
  );
}
